import { useModal } from '@shape-ds/components';
import { ONE_MEGABYTE } from 'config/constants';
import { useGlobalNotification } from 'hooks';
import { CaseForm } from 'queries/cases/types';
import { ChangeEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GenericModal, ModalWidthEnum } from 'ui/GenericModal/GenericModal';
import { returnSameNameFile } from 'utils';

interface UploadIconProps {
  acceptFiles?: string;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

export const UploadIcon = ({ acceptFiles, fileInputRef }: UploadIconProps) => {
  const { t } = useTranslation();
  const { displaySnackbar } = useGlobalNotification();
  const { setValue, getValues } = useFormContext<CaseForm>();
  const evidences = getValues('evidences') || [];
  const { openModal, closeModal } = useModal();

  const acceptedFiles = acceptFiles || '.xls, .png, .txt, .pdf, .doc, .jpg';

  const isFileExtensionValid = (fileName: string, acceptedExtensions: string) => {
    const nameSplit = fileName.split('.');
    const extension = nameSplit[nameSplit.length - 1];

    if (extension && acceptedExtensions.includes(extension.toLowerCase())) return true;
    return false;
  };

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const newEvidences = [...evidences];

    if (!isFileExtensionValid(file.name, acceptedFiles)) {
      displaySnackbar({
        message: t('event_page:snackbarMessages.error.file_extension'),
        type: 'error',
      });
      return;
    }

    if (file.size > 10 * ONE_MEGABYTE) {
      displaySnackbar({
        message: t('event_page:snackbarMessages.error.file_size'),
        type: 'error',
      });
      return;
    }

    if (evidences.length > 0) {
      const repeatedEvidences = returnSameNameFile(
        file.name,
        evidences.map((e) => e.name),
      );
      if (repeatedEvidences) {
        openModal(
          <GenericModal
            title={t('case:messages.delete.title')}
            description={t('case:messages.delete.message')}
            width={ModalWidthEnum.MEDIUM}
            leftBtn={{ style: 'secondary', text: t('case:cancel'), onClick: closeModal }}
            rightBtn={{
              style: 'primary',
              text: t('case:confirm_delete'),
              onClick: () => {
                closeModal();
              },
            }}
          />,
        );
        return;
      }
    }
    newEvidences.push({ file, name: file.name, isNew: true, isDeleted: false });
    setValue('evidences', newEvidences);
  };

  return (
    <input
      name="addEvidences"
      ref={fileInputRef}
      type="file"
      hidden
      data-testid="file-input"
      accept={acceptedFiles}
      onChange={handleUpload}
    />
  );
};
